import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { api } from '@carrent/api'
import StartTripWizard  from './StartTripWizard'
import FinishTripWizard from './FinishTripWizard'
import ActiveTripScreen from './ActiveTripScreen'

export default function DriverHome() {
  const { user, refreshUser } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  const [activeTrip, setActiveTrip]     = useState(null)
  const [activeCar, setActiveCar]       = useState(null)
  const [cars, setCars]                 = useState([])
  const [loading, setLoading]           = useState(true)
  // Если пришли с CarsPage — сразу открываем визард для выбранной машины
  const [selectedCar, setSelectedCar]   = useState(location.state?.startTrip || null)
  const [showStart, setShowStart]       = useState(!!location.state?.startTrip)
  const [showFinish, setShowFinish]     = useState(false)
  const [tripFinished, setTripFinished] = useState(null) // завершённая поездка

  const load = async () => {
    setLoading(true)
    const fresh = await refreshUser()
    const status = fresh?.status ?? user?.status

    if (status !== 'approved') { setLoading(false); return }

    try {
      const [tripRes, carsRes] = await Promise.all([
        api.get('/trips/active').catch(() => ({ data: { data: null } })),
        api.get('/cars/available').catch(() => ({ data: { data: [] } })),
      ])
      const trip = tripRes.data?.data
      setActiveTrip(trip)
      setCars(carsRes.data?.data || [])

      // Загрузить данные машины для активной поездки
      if (trip?.car_id) {
        api.get(`/cars/${trip.car_id}`)
          .then(r => setActiveCar(r.data?.data))
          .catch(() => {})
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Доброе утро' : hour < 17 ? 'Добрый день' : 'Добрый вечер'
  const status = user?.status

  // ── Визард старта ────────────────────────────────────────────
  if (showStart && selectedCar) {
    return (
      <StartTripWizard
        car={selectedCar}
        onCancel={() => { setShowStart(false); setSelectedCar(null) }}
        onStarted={(trip) => {
          setShowStart(false)
          setSelectedCar(null)
          setActiveTrip(trip)
          load()
        }}
      />
    )
  }

  // ── Визард завершения ─────────────────────────────────────────
  if (showFinish && activeTrip) {
    return (
      <FinishTripWizard
        trip={activeTrip}
        car={activeCar}
        onCancel={() => setShowFinish(false)}
        onFinished={(finished) => {
          setShowFinish(false)
          setTripFinished(finished)
          setActiveTrip(null)
          load()
        }}
      />
    )
  }

  // ── Экран "Поездка завершена" ─────────────────────────────────
  if (tripFinished) {
    const hasDamage = tripFinished.has_damage
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: 'var(--gap)',
        background: 'var(--bg)', textAlign: 'center', gap: 'var(--gap)' }}>
        <div style={{ fontSize: '4rem' }}>{hasDamage ? '⚠️' : '✅'}</div>
        <h2>{hasDamage ? 'Поездка завершена' : 'Поездка завершена!'}</h2>
        <p className="text-muted" style={{ lineHeight: 1.7 }}>
          {hasDamage
            ? 'Повреждения зафиксированы. Машина отправлена на осмотр. Администратор свяжется с вами.'
            : 'Данные переданы администратору. После проверки поездка будет подтверждена.'}
        </p>
        <div className="card" style={{ width: '100%', textAlign: 'left', padding: 0, overflow: 'hidden' }}>
          {[
            ['Пройдено', `${tripFinished.distance?.toLocaleString() || 0} км`],
            ['Начало',   new Date(tripFinished.started_at).toLocaleString('ru', { dateStyle: 'medium', timeStyle: 'short' })],
            ['Конец',    tripFinished.finished_at ? new Date(tripFinished.finished_at).toLocaleString('ru', { dateStyle: 'medium', timeStyle: 'short' }) : '—'],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '10px var(--gap)', borderBottom: '1px solid var(--border)' }}>
              <span className="text-muted" style={{ fontSize: '0.85rem' }}>{l}</span>
              <span style={{ fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
        <button className="btn btn--primary btn--full"
          onClick={() => { setTripFinished(null); load() }}>
          На главную
        </button>
      </div>
    )
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div className="spinner" style={{ width: 40, height: 40 }} />
    </div>
  )

  // ── Активная поездка ──────────────────────────────────────────
  if (status === 'approved' && activeTrip) {
    return (
      <ActiveTripScreen
        trip={activeTrip}
        car={activeCar}
        onFinish={() => setShowFinish(true)}
      />
    )
  }

  // ── Главный экран ──────────────────────────────────────────────
  return (
    <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>
      <div style={{ paddingTop: 'var(--gap)' }}>
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>{greeting},</p>
        <h1 style={{ fontSize: '1.4rem', marginTop: 2 }}>
          {user?.full_name?.split(' ')[0]} 👋
        </h1>
      </div>

      {/* Статусные карточки */}
      {status === 'draft' && (
        <div className="card" style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>📋 Завершите регистрацию</p>
          <p className="text-muted" style={{ fontSize: '0.88rem', marginBottom: 'var(--gap-sm)' }}>
            Загрузите паспорт и фото для верификации
          </p>
          <button className="btn btn--primary" onClick={() => navigate('/verify')}>Продолжить →</button>
        </div>
      )}

      {status === 'pending' && (
        <div className="card" style={{ borderColor: 'rgba(59,130,246,0.4)', background: 'rgba(59,130,246,0.06)', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>⏳</div>
          <p style={{ fontWeight: 700 }}>Документы на проверке</p>
          <p className="text-muted" style={{ fontSize: '0.88rem', marginTop: 8 }}>
            Обычно это занимает до 24 часов
          </p>
        </div>
      )}

      {status === 'rejected' && (
        <div className="card" style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.06)' }}>
          <p style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>❌ Заявка отклонена</p>
          <p className="text-muted" style={{ fontSize: '0.88rem', marginBottom: 'var(--gap-sm)' }}>
            {user.admin_comment || 'Обратитесь к администратору'}
          </p>
          <button className="btn btn--outline btn--full" onClick={() => navigate('/verify')}>
            Исправить и подать повторно →
          </button>
        </div>
      )}

      {status === 'blocked' && (
        <div className="card" style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.06)' }}>
          <p style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>🚫 Аккаунт заблокирован</p>
          <p className="text-muted" style={{ fontSize: '0.88rem' }}>{user.blocked_reason || 'Обратитесь к администратору'}</p>
        </div>
      )}

      {/* Доступные машины */}
      {status === 'approved' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p className="section-title" style={{ margin: 0 }}>Доступные машины</p>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>{cars.length} авто</span>
          </div>

          {cars.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--gap-lg)' }}>
              <p style={{ fontSize: '2rem', marginBottom: 8 }}>😔</p>
              <p className="text-muted">Нет доступных машин. Попробуйте позже.</p>
            </div>
          ) : cars.slice(0, 3).map(car => (
            <div key={car.id} className="card"
              style={{ display: 'flex', gap: 'var(--gap-sm)', alignItems: 'center' }}>
              <div style={{ width: 60, height: 60, borderRadius: 'var(--r-sm)', flexShrink: 0,
                background: 'var(--bg)', border: '1px solid var(--border)', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {car.photo_main
                  ? <img src={car.photo_main} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: '1.5rem' }}>🚗</span>
                }
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 700 }}>{car.brand} {car.model}</p>
                <p className="text-muted" style={{ fontSize: '0.78rem' }}>
                  {car.plate} · {car.year}
                </p>
              </div>
              <button className="btn btn--primary"
                style={{ flexShrink: 0, padding: '8px 16px' }}
                onClick={() => { setSelectedCar(car); setShowStart(true) }}>
                Выбрать
              </button>
            </div>
          ))}

          {cars.length > 3 && (
            <button className="btn btn--outline btn--full" onClick={() => navigate('/cars')}>
              Все машины ({cars.length}) →
            </button>
          )}
        </>
      )}

      {/* Быстрые ссылки */}
      <p className="section-title">Мой аккаунт</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
        {[
          { icon: '📋', label: 'Мои поездки', to: '/trips' },
          { icon: '👤', label: 'Профиль',     to: '/profile' },
        ].map(({ icon, label, to }) => (
          <div key={to} className="card"
            style={{ textAlign: 'center', cursor: 'pointer', padding: 'var(--gap-sm)' }}
            onClick={() => navigate(to)}>
            <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>{icon}</div>
            <p style={{ fontSize: '0.88rem' }}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
